import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { PlatformControl } from '@/lib/platform/control';
import { signalDestination } from '@/lib/platform/control-nav';

function SignalLink({ signalKey, className, children }: { signalKey: string; className: string; children: ReactNode }) {
  const href = signalDestination(signalKey);
  if (href === null) return <div className={className}>{children}</div>;
  return <Link to={href} className={`${className} block`}>{children}</Link>;
}

function stateClass(state: string): string {
  if (state === 'healthy') return 'text-success';
  if (state === 'degraded' || state === 'stale') return 'text-warning';
  if (state === 'unavailable') return 'text-danger';
  return 'text-muted';
}

const HIDDEN_SIGNALS = new Set(['memory', 'frontend', 'deploy', 'cache']);

export function SignalGrid({ control }: { control: PlatformControl }) {
  const visible = control.signals.filter((signal) => !HIDDEN_SIGNALS.has(signal.key) && !(signal.key === 'redis' && signal.state === 'unconfigured'));
  const attention = visible.filter((signal) => signal.state === 'degraded' || signal.state === 'unavailable' || signal.state === 'stale');
  const rest = visible.filter((signal) => !attention.includes(signal));
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">Checked {control.checkedAt}. This snapshot is cached for about a minute.</p>
      {attention.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {attention.map((signal) => (
            <SignalLink key={signal.key} signalKey={signal.key} className="rounded-2xl border border-warning/40 bg-card px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-ink">{signal.label}</p>
                <p className={`text-xs font-semibold capitalize ${stateClass(signal.state)}`}>{signal.state}</p>
              </div>
              <p className="mt-2 text-xs text-muted">{signal.detail}</p>
            </SignalLink>
          ))}
        </div>
      ) : (
        <p className="rounded-2xl border border-line bg-card px-4 py-3 text-sm text-success">Nothing measured is degraded or stale.</p>
      )}
      <div className="overflow-hidden rounded-2xl border border-line bg-card">
        {rest.map((signal) => (
          <SignalLink key={signal.key} signalKey={signal.key} className="grid gap-1 border-b border-line px-4 py-3 last:border-b-0 hover:bg-shell/40 sm:grid-cols-[10rem_6rem_minmax(0,1fr)] sm:items-baseline sm:gap-3">
            <p className="text-sm font-semibold text-ink">{signal.label}</p>
            <p className={`text-xs font-semibold capitalize ${stateClass(signal.state)}`}>{signal.state}</p>
            <p className="text-xs text-muted">{signal.detail}</p>
          </SignalLink>
        ))}
      </div>
      <p className="text-xs text-muted">Host memory, the frontend process, the last deployment, and cache connectivity are not measured here.</p>
    </div>
  );
}

export function DatabaseList({
  control,
  busy,
  onRecheck,
}: {
  control: PlatformControl;
  busy: boolean;
  onRecheck: (slug: string) => void;
}) {
  if (control.tenants.length === 0) {
    return <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No agencies are registered.</p>;
  }
  return (
    <div className="space-y-2">
      {control.tenants.map((tenant) => (
        <article key={tenant.slug} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-card px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-ink">{tenant.name}</p>
            <p className="text-xs text-muted">{tenant.slug} · {tenant.status} · <span className={`font-semibold capitalize ${stateClass(tenant.database)}`}>{tenant.database}</span></p>
            <p className="text-xs text-muted">{tenant.detail}</p>
          </div>
          <button type="button" disabled={busy} onClick={() => onRecheck(tenant.slug)} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Recheck</button>
        </article>
      ))}
      <p className="text-xs text-muted">Schema repair and a new backup are on <Link to="/super-admin/agencies" className="underline">Agencies</Link>.</p>
    </div>
  );
}

export function JobsPanel({
  control,
  busy,
  onRetry,
}: {
  control: PlatformControl;
  busy: boolean;
  onRetry: (id: string) => void;
}) {
  const failed = control.failedJobs;
  return (
    <div className="space-y-3">
      {failed.state === 'unknown' && <p className="rounded-2xl border border-line bg-card px-4 py-6 text-sm text-muted">Failed-job storage is not installed, so there is nothing to retry.</p>}
      {failed.state === 'unavailable' && <p className="rounded-2xl border border-line bg-card px-4 py-6 text-sm text-danger">Failed jobs could not be read.</p>}
      {failed.count === 0 && failed.state !== 'unknown' && failed.state !== 'unavailable' && <p className="rounded-2xl border border-line bg-card px-4 py-6 text-sm text-muted">No failed jobs.</p>}
      {failed.count > failed.jobs.length && <p className="text-xs text-muted">Showing {failed.jobs.length} of {failed.count} failed jobs.</p>}
      {failed.jobs.map((job) => (
        <article key={job.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-card px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-ink">{job.name}</p>
            <p className="text-xs text-muted">{job.failedAt ?? 'Unknown time'}</p>
          </div>
          <button type="button" disabled={busy} onClick={() => onRetry(job.id)} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Retry</button>
        </article>
      ))}
    </div>
  );
}

export function BackupPanel({ control }: { control: PlatformControl }) {
  return (
    <div className="space-y-3">
      {control.archives.length === 0 && (
        <p className="rounded-2xl border border-line bg-card px-4 py-6 text-sm text-muted">No archive is stored yet. Start one from an agency page.</p>
      )}
      {control.archives.map((archive) => (
        <article key={archive.id} className="rounded-2xl border border-line bg-card px-4 py-3 text-sm">
          <p className="font-semibold text-ink">{archive.id}</p>
          <p className="text-xs text-muted">{archive.createdAt ?? 'Unknown time'} · {archive.slugs.join(', ') || 'No agency recorded'} · {archive.checksumRecorded ? 'Checksum recorded' : 'Checksum missing'} · {archive.bytes === null ? 'Size unknown' : `${archive.bytes} bytes`}</p>
        </article>
      ))}
      <p className="text-xs text-muted">Restore stays a scratch command. It is not a button on this page.</p>
    </div>
  );
}
